// THE SELECTED TILE (docs SIGNOFF-SPEC S1, right): its key and extents, how far it is
// signed off, what it holds, the schedule activities on it, and who signed.
import React from "react";
import { Button, Empty } from "antd";
import { StarFilled } from "@ant-design/icons";
import moment from "moment";
import { BLUE, GOLD, RING, tileColor, tileHolds } from "./signoffState";

const pct = (v) => `${Math.round(v * 100)}%`;
const fmt = (v) => (Math.round(v * 10) / 10).toLocaleString();

function Star({ level }) {
  if (level >= GOLD) return <StarFilled style={{ color: RING.tenant }} />;
  if (level >= BLUE) return <StarFilled style={{ color: RING.ownerGc }} />;
  return null;
}

export default function TilePanel({ tile, tiles, states, areas, levels, rings, at, canRequest, onRequest, onOpen3d, volumes = new Map(), vols = [] }) {
  if (!tile) {
    return (
      <div className="signoff-tile-panel">
        <div className="signoff-panel-title">Selected tile</div>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Click a tile in the model" />
      </div>
    );
  }
  const g = tiles.grid;
  const u = g.unitM;
  const ext = (o, n) => `${fmt((o + n * g.edge) * u)}–${fmt((o + (n + 1) * g.edge) * u)} m`;
  const color = tileColor(tile, states, vols);
  const placed = (tile.g || []).reduce((s, x) => s + x.n, 0);
  const byLevel = {};
  const byArea = {};
  const bySystem = {};
  for (const x of tile.g || []) {
    byLevel[x.l] = (byLevel[x.l] || 0) + x.n;
    if (x.a) byArea[x.a] = (byArea[x.a] || 0) + x.n;
    for (const sys of x.s || []) bySystem[sys] = (bySystem[sys] || 0) + x.n;
  }
  // a system is as far as its best sign-off on any area the tile holds it in
  const systemLevel = (sys) => Math.max(0, ...(tile.g || []).filter((x) => (x.s || []).includes(sys))
    .flatMap((x) => [x.a, ...(x.aa || [])]).filter(Boolean).map((a) => states.get(`system|${a}|${sys.toLowerCase()}`) || 0));
  const holding = (rings || []).filter((r) => r.type === "signoff" && r.target && tileHolds(tile, r.target, vols) && new Date(r.at) <= at);
  const lastSign = holding.find((r) => !r.withdrawn);
  const acts = [];
  const seen = new Set();
  for (const r of holding) {
    (r.activities || []).forEach((name) => { if (!seen.has(name)) { seen.add(name); acts.push({ name, at: r.at, level: r.level }); } });
  }
  return (
    <div className="signoff-tile-panel" data-testid="signoff-tile-panel">
      <div className="signoff-panel-title">Selected tile</div>
      <div className="tile-key">{tile.key}</div>
      <small className="signoff-muted">x {ext(g.origin.x, tile.i)} · y {ext(g.origin.z, tile.j)} · z {ext(g.origin.y, tile.k)}</small>
      {placed || vols.length ? (
        <div className={`tile-share ${color.partial ? "is-partial" : ""}`}>
          {color.partial ? "Partial: " : ""}{pct(color.blue)} owner/GC <StarFilled style={{ color: RING.ownerGc }} /> · {pct(color.gold)} contractor <StarFilled style={{ color: RING.tenant }} />
        </div>
      ) : null}

      {vols.length ? (
        <>
          <div className="tile-section">3D volumes</div>
          {vols.map((id) => volumes.get(id)).filter(Boolean).map((v) => (
            <div key={`v-${v.id}`} className="tile-row"><span><i className="tile-swatch" style={{ background: v.color }} /> {v.parentId && volumes.get(v.parentId) ? `${volumes.get(v.parentId).name} › ` : ""}{v.name} <Star level={states.get(`volume|${v.id}`) || 0} /></span></div>
          ))}
        </>
      ) : null}
      <div className="tile-section">Contains</div>
      {Object.entries(byLevel).sort().map(([l, n]) => (
        <div key={`l-${l}`} className="tile-row"><span>{levels[l] || l || "No level"} <Star level={states.get(`level|${l}`) || 0} /></span><span>{pct(n / placed)}</span></div>
      ))}
      {Object.entries(byArea).sort((a, b) => b[1] - a[1]).map(([a, n]) => (
        <div key={`a-${a}`} className="tile-row"><span>{(areas[a] && areas[a].name) || "Area"} <Star level={states.get(`area|${a}`) || 0} /></span><span>{pct(n / placed)}</span></div>
      ))}
      {Object.entries(bySystem).sort((a, b) => b[1] - a[1]).map(([sys, n]) => (
        <div key={`s-${sys}`} className="tile-row"><span>{sys} <Star level={systemLevel(sys)} /></span><span>{n.toLocaleString()} elements</span></div>
      ))}
      <div className="tile-row"><span>Elements</span><span>{tile.n.toLocaleString()}</span></div>
      {tile.u ? <div className="tile-row signoff-muted"><span>Not in a drawn area</span><span>{tile.u.toLocaleString()}</span></div> : null}

      {acts.length ? (
        <>
          <div className="tile-section">Schedule activities</div>
          {acts.slice(0, 8).map((a) => (
            <div key={a.name} className="tile-row"><span>{a.name}</span><span><StarFilled style={{ color: RING[a.level] }} /> {moment(a.at).format("MMM D")}</span></div>
          ))}
          {acts.length > 8 ? <small className="signoff-muted">+{acts.length - 8} more</small> : null}
        </>
      ) : null}

      {lastSign ? (
        <>
          <div className="tile-section">Approved parties · {lastSign.target.label}, {moment(lastSign.at).format("MMM D")}</div>
          {(lastSign.parties || []).map((p, i) => (
            <div key={i} className="tile-party">
              <div><b>{p.name || p.email}</b><small className="d-block signoff-muted">{[p.company, p.role].filter(Boolean).join(" · ")}</small></div>
              <div className={p.pending ? "text-warning" : ""}>
                {p.signed ? <><StarFilled style={{ color: RING[p.star] }} /> {moment(p.at).format("MMM D, h:mm A")}</> : p.pending ? `Pending · link expires ${moment(p.expiresAt).format("MMM D")}` : p.outcome === "changes" ? "Asked for changes" : ""}
              </div>
            </div>
          ))}
        </>
      ) : null}

      <div className="d-flex flex-wrap mt-3" style={{ gap: 8 }}>
        {onOpen3d ? <Button onClick={() => onOpen3d(tile)}>Open in 3D viewer</Button> : null}
        {canRequest ? <Button type="primary" onClick={() => onRequest(tile)}>Request sign-off</Button> : null}
      </div>
    </div>
  );
}
